import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import transaction from '../../utils/api/transaction';
import BatteryLoader from '../battery-loader/BatteryLoader';

interface TransactionSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

interface TransactionDataProperties {
  _id: string;
  chargingPointSn: string;
  connector: number;
  status: string;
  dynamicPricing: {
    pricingType: string;
    price: string;
  };
  initialChargePercentage: string;
  desiredChargePercentage: string;
  batteryCapacity: string;
  chargingStation: any[]; // if you have its shape, you can define it better
  stripeCustomerObjectId: string;
  authorizePaymentIntentObjectId: string;
  paymentIntentObjectId: string;
  userCustomId: string;
  accountCustomId: string;
  isGuestUser: boolean;
  createdAt: string;
  updatedAt: string;
  meterStart: string;
  meterStop: string;
  transactionId: number;
  transactionStartsAt: string;
  transactionEndsAt: string;
  transactionEndReason: string;
  walletHistoryId: string;
  isFreeTransaction: boolean;
  isTransactionIncompleteDueToOffline: boolean;
  rfidNo: string;

  sampledData: {
    measurand: string;
    context: string;
    value: string;
    unit: string;
  };

  walletHistory: {
    _id: string;
    type: string;
    amount: string;
    stripeCustomer: string;
    currency: string;
    userCustomId: string;
    isGuestUser: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };

  stripeCustomer: {
    _id: string;
    stripeCustomerId: string;
    paymentMethodId: string;
    cardHolderName: string;
    funding: string;
    brand: string;
    last4: number;
    expiryMonth: number;
    expiryYear: number;
    userCustomId: string;
    isGuestUser: boolean;
    isSelected: boolean;
    deletedAt: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };

  stripeAuthorizePaymentInfo: {
    _id: string;
    paymentIntendId: string;
    stripeCustomerObjectId: string;
    amount: string;
    currency: string;
    status: string;
    userCustomId: string;
    isGuestUser: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };

  stripePaymentInfo: {
    _id: string;
    paymentIntendId: string;
    stripeCustomerObjectId: string;
    amount: string;
    currency: string;
    status: string;
    userCustomId: string;
    isGuestUser: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };

  chargingSchedule: {
    _id: string;
    chargingPointSn: string;
    vehicleId: string;
    connectorId: number;
    startDateUtc: string;
    endDateUtc: string;
    userCustomId: string;
    accountCustomId: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };
}

/**
 * Station side panel
 * @param properties
 * @returns
 */
function TransactionSidePanel(properties: TransactionSidePanelProperties) {
  const { fetchIdFrom, id } = properties;
  const dispatch = useAppDispatch();
  const { t } = useTranslation('tableSidepanel');
  const [searchParameters] = useSearchParams();
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [transactionData, setTransactionData] = useState<TransactionDataProperties>();

  /**
   * getPenaltyModeLabel
   * @param mode - Penalty mode
   * @returns Penalty mode label
   */
  const getPenaltyModeLabel = (mode: string) => {
    switch (mode) {
      case 'grace':
        return t('grace');
      case 'flatRate':
        return t('flatRate');
      case 'perMinute':
        return t('perMinute');
      default:
        return '—';
    }
  };

  /**
   * ceilingTypeMap
   */
  const ceilingTypeMap: Record<string, string> = {
    fixedAmount: t('fixedAmount'),
    percentage: t('percentage'),
  };

  const transactionMap = [
    {
      header: t('transactionDetails'),
      keyValuePair: [
        { label: t('chargingPointSn'), key: 'chargingPointSn' },
        { label: t('connector'), key: 'connector' },
        { label: t('status'), key: 'status' },
        { label: t('transactionStartsAt'), key: 'transactionStartsAt', date: true },
        { label: t('transactionEndsAt'), key: 'transactionEndsAt', date: true },
        { label: t('transactionEndReason'), key: 'transactionEndReason' },
        { label: t('initialChargePercentage'), key: 'initialChargePercentage' },
        { label: t('desiredChargePercentage'), key: 'desiredChargePercentage' },
        { label: t('batteryCapacity'), key: 'batteryCapacity' },
        { label: t('meterStart'), key: 'meterStart' },
        { label: t('meterStop'), key: 'meterStop' },
        { label: t('isFreeTransaction'), key: 'isFreeTransaction' },
        {
          label: t('isTransactionIncompleteDueToOffline'),
          key: 'isTransactionIncompleteDueToOffline',
        },
        {
          label: t('rfid'),
          key: 'rfidNo',
        },
      ],
    },
    {
      header: t('idleStatePenalty'),
      keyValuePair: [
        {
          label: t('totalIdleMinutes'),
          key: 'penaltyDetails.totalMinutes',
          /**
           * Rendering custom style data
           * @param value
           * @returns
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('totalIdleMinutes')}</h2>
              <p>
                {value?.penaltyDetails?.totalMinutes
                  ? `${value.penaltyDetails.totalMinutes} min`
                  : '—'}
              </p>
            </div>
          ),
        },
        {
          label: t('maxCeilingType'),
          key: 'penaltyDetails.ceilingType',
          /**
           * maxCeilingType
           * @param value
           * @returns
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('maxCeilingType')}</h2>
              <p>{ceilingTypeMap[value?.penaltyDetails?.ceilingType] || '—'}</p>
            </div>
          ),
        },

        {
          label: t('maxCeilingValue'),
          key: 'penaltyDetails.ceilingValue',
        },
        {
          label: t('graceTimeLimit'),
          key: 'penaltyDetails.graceTime',
          /**
           * Rendering custom style data
           * @param value
           * @returns
           */
          view: (value: any) => (
            <div className="transaction-item">
              <h2>{t('graceTimeLimit')}</h2>
              <p>
                {value?.penaltyDetails?.graceTime ? `${value.penaltyDetails.graceTime} min` : '—'}
              </p>
            </div>
          ),
        },

        /* ✅ Dynamic duration slabs */
        {
          label: t('idlePenaltySlabs'),
          key: 'penaltyDetails.durationAmountList',
          /**
           * dd
           * @param value
           * @returns
           */
          view: (value: any) => {
            const list = value?.penaltyDetails?.durationAmountList;

            if (!Array.isArray(list) || list.length === 0) {
              return (
                <div className="transaction-item">
                  <h2>{t('idlePenaltySlabs')}</h2>
                  <p>—</p>
                </div>
              );
            }

            return (
              <div className="transaction-item">
                {list.map((item: any, index: number) => {
                  return (
                    <>
                      <div key={item._id || index}>
                        <h2 style={{ flex: 1 }}>
                          {item.from} min – {item.to} min ({getPenaltyModeLabel(item.mode)})
                        </h2>
                      </div>
                      <div key={item._id || index}>
                        <p>{item.amount} </p>
                      </div>
                    </>
                  );
                })}
              </div>
            );
          },
        },
      ],
    },

    {
      header: t('dynamicPricing'),
      keyValuePair: [
        { label: t('pricingType'), key: 'dynamicPricing.pricingType' },
        { label: t('price'), key: 'dynamicPricing.price' },
      ],
    },
    {
      header: t('revenueDetails'),
      keyValuePair: [
        { label: t('revenue'), key: 'revenue.foRevenue' },
        {
          label: t('electricityCost'),
          key: 'revenue.electricityCostByUtilityProvider',
        },
      ],
    },
    {
      header: t('sampledData'),
      keyValuePair: [
        { label: t('measurand'), key: 'sampledData.measurand' },
        { label: t('context'), key: 'sampledData.context' },
        { label: t('value'), key: 'sampledData.value' },
        { label: t('unit'), key: 'sampledData.unit' },
      ],
    },
    {
      header: t('walletHistory'),
      keyValuePair: [
        { label: t('walletHistory'), key: 'walletHistory._id' },
        { label: t('type'), key: 'walletHistory.type' },
        { label: t('amount'), key: 'walletHistory.amount' },
        // { label: 'Currency', key: 'walletHistory.currency' },
        { label: t('stripeCustomerRef'), key: 'walletHistory.stripeCustomer' },
        { label: t('userCustomId'), key: 'walletHistory.userCustomId' },
        { label: t('isGuestUser'), key: 'walletHistory.isGuestUser' },
        { label: t('createdAt'), key: 'walletHistory.createdAt', date: true },
      ],
    },
    // {
    //   header: 'Stripe Customer',
    //   keyValuePair: [
    //     { label: 'Stripe Customer Object ID', key: 'stripeCustomer._id' },
    //     { label: 'Stripe Customer ID', key: 'stripeCustomer.stripeCustomerId' },
    //     { label: 'Payment Method ID', key: 'stripeCustomer.paymentMethodId' },
    //     { label: 'Card Holder Name', key: 'stripeCustomer.cardHolderName' },
    //     { label: 'Funding', key: 'stripeCustomer.funding' },
    //     { label: 'Card Brand', key: 'stripeCustomer.brand' },
    //     { label: 'Card Last 4 Digits', key: 'stripeCustomer.last4' },
    //     { label: 'Expiry Month', key: 'stripeCustomer.expiryMonth' },
    //     { label: 'Expiry Year', key: 'stripeCustomer.expiryYear' },
    //     { label: 'User Custom ID', key: 'stripeCustomer.userCustomId' },
    //     { label: 'Is Guest User', key: 'stripeCustomer.isGuestUser' },
    //     { label: 'Is Selected', key: 'stripeCustomer.isSelected' },
    //     { label: 'Created At', key: 'stripeCustomer.createdAt', date: true },
    //   ],
    // },
    // {
    //   header: 'Stripe Authorized Payment',
    //   keyValuePair: [
    //     { label: 'Authorized Payment ID', key: 'stripeAuthorizePaymentInfo._id' },
    //     { label: 'Payment Intent ID', key: 'stripeAuthorizePaymentInfo.paymentIntendId' },
    //     { label: 'Stripe Customer Ref', key: 'stripeAuthorizePaymentInfo.stripeCustomerObjectId' },
    //     { label: 'Amount', key: 'stripeAuthorizePaymentInfo.amount' },
    //     { label: 'Currency', key: 'stripeAuthorizePaymentInfo.currency' },
    //     { label: 'Status', key: 'stripeAuthorizePaymentInfo.status' },
    //     { label: 'User Custom ID', key: 'stripeAuthorizePaymentInfo.userCustomId' },
    //     { label: 'Is Guest User', key: 'stripeAuthorizePaymentInfo.isGuestUser' },
    //     { label: 'Created At', key: 'stripeAuthorizePaymentInfo.createdAt', date: true },
    //   ],
    // },
    // {
    //   header: 'Stripe Final Payment',
    //   keyValuePair: [
    //     { label: 'Final Payment ID', key: 'stripePaymentInfo._id' },
    //     { label: 'Payment Intent ID', key: 'stripePaymentInfo.paymentIntendId' },
    //     { label: 'Stripe Customer Ref', key: 'stripePaymentInfo.stripeCustomerObjectId' },
    //     { label: 'Amount', key: 'stripePaymentInfo.amount' },
    //     { label: 'Currency', key: 'stripePaymentInfo.currency' },
    //     { label: 'Status', key: 'stripePaymentInfo.status' },
    //     { label: 'User Custom ID', key: 'stripePaymentInfo.userCustomId' },
    //     { label: 'Is Guest User', key: 'stripePaymentInfo.isGuestUser' },
    //     { label: 'Created At', key: 'stripePaymentInfo.createdAt', date: true },
    //   ],
    // },
    {
      header: t('chargingSchedule'),
      keyValuePair: [
        { label: t('scheduleId'), key: 'chargingSchedule._id' },
        { label: t('scheduleChargingPointSn'), key: 'chargingSchedule.chargingPointSn' },
        { label: t('vehicleId'), key: 'chargingSchedule.vehicleId' },
        { label: t('connectorId'), key: 'chargingSchedule.connectorId' },
        { label: t('startDate'), key: 'chargingSchedule.startDateUtc', date: true },
        { label: t('endDate'), key: 'chargingSchedule.endDateUtc', date: true },
        { label: t('userCustomId'), key: 'chargingSchedule.userCustomId' },
        { label: t('accountCustomId'), key: 'chargingSchedule.accountCustomId' },
        { label: t('status'), key: 'chargingSchedule.status' },
        { label: t('createdAt'), key: 'chargingSchedule.createdAt', date: true },
      ],
    },
    {
      header: t('userInfo'),
      keyValuePair: [
        { label: t('userCustomId'), key: 'userCustomId' },
        { label: t('accountCustomId'), key: 'accountCustomId' },
        { label: t('isGuestUser'), key: 'isGuestUser' },
      ],
    },
    // {
    //   header: 'Meta Info',
    //   keyValuePair: [
    //     { label: 'Transaction Object ID', key: '_id' },
    //     { label: 'Wallet History ID', key: 'walletHistoryId' },
    //     { label: 'Stripe Customer Ref ID', key: 'stripeCustomerObjectId' },
    //     { label: 'Authorized Payment Ref ID', key: 'authorizePaymentIntentObjectId' },
    //     { label: 'Final Payment Ref ID', key: 'paymentIntentObjectId' },
    //     { label: 'Created At', key: 'createdAt', date: true },
    //   ],
    // },
  ];

  /**
   * Function to fetch station details
   * @param transactionId
   */
  const handleFetchStationDetails = async (transactionId: string) => {
    setIsLoader(true);
    transaction
      .fetchTransactionById(transactionId)
      .then((response) => {
        setTransactionData(response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      })
      .finally(() => {
        setIsLoader(false);
      });
  };

  /**
   * Function to get nested values from transactionData using dot notation
   * @param path dot-separated key path
   * @returns value or undefined
   */
  const getNestedValue = (path: string, utcToDate: boolean): any => {
    let value;
    try {
      value = path
        .split('.')
        .reduce((accumulator, key) => accumulator?.[key], transactionData as any);
      if (utcToDate) {
        value = `${ConvertUTCtoDate(value)} - ${FormatUtcString(ConvertUTCtoHHMM(value))}`;
      }
    } catch (error) {
      console.error(error);
    }
    return value;
  };

  useEffect(() => {
    let transactionObjectId;
    if (fetchIdFrom === 'queryParams') {
      transactionObjectId = searchParameters.get('transactionObjectId');
    } else if (fetchIdFrom === 'props') {
      transactionObjectId = id;
    }

    if (!transactionObjectId) {
      return;
    }

    handleFetchStationDetails(transactionObjectId);
  }, [searchParameters, id]);

  if (isLoader) {
    return (
      <div className="loader-wrap">
        <div className="loader">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="transaction-side-panel-wrap">
      <div className="transaction-side-panel-container">
        {transactionMap.map((section) => (
          <div className="transaction-section">
            <h1>{section.header}</h1>
            {section.keyValuePair.map((item: any) => {
              const value = getNestedValue(item.key, item.date || false);
              return item.view ? (
                item.view(transactionData)
              ) : (
                <div className="transaction-item">
                  <h2>{item.label}: </h2>
                  <p>{value !== undefined && value !== null ? String(value) : '—'}</p>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default TransactionSidePanel;
